/**
 * Pre-Pack queue — top-down ordered list.
 *
 * The API returns cartons sorted by ship-by date, then shipment id, then carton
 * number. The first carton that still has pending work is the primary card; if
 * every carton in the current filter is fully packed (for example a search that
 * only matches completed shipments), the first matching carton is still shown so
 * the list does not disappear.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ListOrdered,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { useAuth } from "@/components/AuthProvider";
import { cn } from "@/lib/utils";

import {
  isolatePrePackQueueCardClick,
  PrePackCartonBatchLink,
  PrePackCartonShipmentLink,
} from "./prepackCartonEntityLinks";
import { PrePackCartonOperatorStack } from "./PrePackCartonOperatorStack";
import { formatPackQty } from "./formatPackQty";
import { cartonOverTargetCount } from "./prepackSealCarton";
import {
  formatKitLineProgress,
  formatLineDeviceProgress,
  packedPhysicalSerials,
  pendingPhysicalSerials,
  plannedPhysicalSerials,
} from "./prepackPhysicalDevices";
import {
  fetchPrePackQueue,
  fetchPrePackActiveCartonOperators,
  fetchPrePackActiveTransferOperators,
  formatShipDate,
  prePackActiveCartonOperatorsQueryKey,
  prePackActiveTransferOperatorsQueryKey,
  type CartonItem,
  type CartonRow,
  type PrePackCartonOperator,
  type PrePackTransferOperator,
  type TransferQueueRow,
} from "./prepackApi";
import { cartonPath, transferPath } from "./prepackQueueNavigation";
import {
  formatTransferRouteLabel,
  mergePrePackQueue,
  pickNextQueueEntry,
  type PrePackQueueEntry,
} from "./prepackQueueMerge";
import { PREPACK_LABOR_FLASH_STORAGE_KEY } from "./prepackLaborFlash";

export const QUEUE_KEY = ["prepack", "queue"] as const;

/** Carton-only slice — must not share a React Query key with {@link QUEUE_KEY} (full `{ cartons, transfers }`). */
export const QUEUE_CARTONS_KEY = ["prepack", "queue", "cartons"] as const;

/** First entry in list order that still has pending work. */
function pickNextEntry(entries: PrePackQueueEntry[]): PrePackQueueEntry | null {
  return pickNextQueueEntry(entries);
}

export function PrePackQueuePage() {
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;
  const [showFullyPacked, setShowFullyPacked] = useState(false);
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [laborReturnFlash, setLaborReturnFlash] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PREPACK_LABOR_FLASH_STORAGE_KEY);
      if (raw != null && raw.trim() !== "") {
        sessionStorage.removeItem(PREPACK_LABOR_FLASH_STORAGE_KEY);
        setLaborReturnFlash(raw.trim());
      }
    } catch {
      /* private mode or storage unavailable */
    }
  }, []);

  const queueQuery = useQuery({
    queryKey: [...QUEUE_KEY, showFullyPacked] as const,
    queryFn: () => fetchPrePackQueue(showFullyPacked),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const operatorsQuery = useQuery({
    queryKey: prePackActiveCartonOperatorsQueryKey,
    queryFn: fetchPrePackActiveCartonOperators,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const transferOperatorsQuery = useQuery({
    queryKey: prePackActiveTransferOperatorsQueryKey,
    queryFn: fetchPrePackActiveTransferOperators,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const operatorsByRowKey = useMemo(() => {
    const map = new Map<string, PrePackCartonOperator[]>();
    for (const operator of operatorsQuery.data ?? []) {
      const key = `${operator.shipmentId}|${operator.cartonNumber}`;
      const list = map.get(key) ?? [];
      list.push(operator);
      map.set(key, list);
    }
    for (const [, list] of map) {
      list.sort((left, right) => left.email.localeCompare(right.email));
    }
    return map;
  }, [operatorsQuery.data]);

  const operatorsByTransferOrderId = useMemo(() => {
    const map = new Map<number, PrePackTransferOperator[]>();
    for (const operator of transferOperatorsQuery.data ?? []) {
      const list = map.get(operator.transferOrderId) ?? [];
      list.push(operator);
      map.set(operator.transferOrderId, list);
    }
    for (const [, list] of map) {
      list.sort((left, right) => left.email.localeCompare(right.email));
    }
    return map;
  }, [transferOperatorsQuery.data]);

  const cartons = queueQuery.data?.cartons ?? [];
  const transfers = queueQuery.data?.transfers ?? [];
  const queueEntries = useMemo(
    () => mergePrePackQueue(cartons, transfers),
    [cartons, transfers],
  );
  const filteredEntries = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return queueEntries;
    return queueEntries.filter((entry) => {
      if (entry.kind === "shipment-carton") {
        const carton = entry.carton;
        if (String(carton.shipmentId).includes(needle)) return true;
        if (String(carton.batchId).includes(needle)) return true;
        return carton.items.some((item) => item.sku.toLowerCase().includes(needle));
      }
      const transfer = entry.transfer;
      if (String(transfer.transferOrderId).includes(needle)) return true;
      if (transfer.transferNumber?.toLowerCase().includes(needle)) return true;
      return formatTransferRouteLabel(transfer).toLowerCase().includes(needle);
    });
  }, [queueEntries, search]);

  const nextWorkEntry = useMemo(() => pickNextEntry(filteredEntries), [filteredEntries]);
  const heroEntry = useMemo((): PrePackQueueEntry | null => {
    const head = filteredEntries[0];
    if (head === undefined) return null;
    return pickNextEntry(filteredEntries) ?? head;
  }, [filteredEntries]);
  const laterEntries = useMemo(() => {
    if (!heroEntry) return filteredEntries;
    return filteredEntries.filter((entry) => entry.rowKey !== heroEntry.rowKey);
  }, [filteredEntries, heroEntry]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageTitleWithHelp
            hoverHint="Shipment cartons and transfer orders are listed by ship-by date. Open a shipment carton to scan devices, or a transfer to pack mixed-SKU cartons."
          >
            Pre-Pack queue
          </PageTitleWithHelp>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void queueQuery.refetch()}
            disabled={queueQuery.isFetching}
          >
            {queueQuery.isFetching ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            Refresh
          </Button>
        </div>
      </header>

      {laborReturnFlash ? (
        <div
          className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900"
          role="status"
        >
          <p className="min-w-0 flex-1 leading-snug">{laborReturnFlash}</p>
          <Button size="sm" variant="secondary" type="button" onClick={() => setLaborReturnFlash(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {actionError ? (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{actionError}</span>
          <button
            type="button"
            className="ml-auto shrink-0 text-rose-600 hover:text-rose-800"
            onClick={() => setActionError(null)}
            aria-label="Dismiss error"
          >
            <XCircle className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="grid gap-3 md:grid-cols-[minmax(16rem,24rem)_1fr] md:items-end">
            <div>
              <Label htmlFor="prepack-queue-search">Search queue</Label>
              <Input
                id="prepack-queue-search"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter by SKU, shipment id, transfer number, or route..."
                autoComplete="off"
              />
            </div>
            <label className="flex h-9 items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={showFullyPacked}
                onChange={(event) => setShowFullyPacked(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Show fully-packed work
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Showing {filteredEntries.length} of {queueEntries.length} item
            {queueEntries.length !== 1 ? "s" : ""} ({cartons.length} shipment carton
            {cartons.length !== 1 ? "s" : ""}, {transfers.length} transfer
            {transfers.length !== 1 ? "s" : ""}). Fully-packed toggle adds completed work only.
          </p>
        </CardBody>
      </Card>

      {queueQuery.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading queue…
        </div>
      ) : queueQuery.isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Could not load the pre-pack queue:{" "}
          {queueQuery.error instanceof Error ? queueQuery.error.message : "unknown error"}
        </p>
      ) : filteredEntries.length === 0 ? (
        <Card>
          <CardBody>
            <p className="py-8 text-center text-sm text-slate-500">
              {showFullyPacked
                ? "No queue items match the current filter."
                : "Nothing needs work right now. Toggle 'Show fully-packed work' to see completed cartons and transfers."}
            </p>
          </CardBody>
        </Card>
      ) : !heroEntry ? (
        <Card>
          <CardBody>
            <p className="py-8 text-center text-sm text-slate-500">No queue items match the current filter.</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-6">
          {nextWorkEntry ? null : (
            <Card className="border-sky-200 bg-sky-50/60">
              <CardBody className="flex items-start gap-3 py-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" aria-hidden />
                <p className="text-sm text-sky-900">
                  Every item in this view is fully packed. You can still open them below — for example to reprint labels
                  or verify contents.
                </p>
              </CardBody>
            </Card>
          )}
          <section aria-labelledby="prepack-next-heading">
            <h2 id="prepack-next-heading" className="sr-only">
              Next item
            </h2>
            <NextQueueHero
              entry={heroEntry}
              operators={
                heroEntry.kind === "shipment-carton"
                  ? operatorsByRowKey.get(heroEntry.carton.rowKey) ?? []
                  : operatorsByTransferOrderId.get(heroEntry.transfer.transferOrderId) ?? []
              }
              viewerUserId={viewerUserId}
            />
          </section>

          {laterEntries.length > 0 ? (
            <section aria-labelledby="prepack-after-heading" className="space-y-2">
              <h2 id="prepack-after-heading" className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <ListOrdered className="h-4 w-4 text-slate-500" aria-hidden />
                Rest of queue
              </h2>
              <ol className="list-none space-y-2 pl-0">
                {laterEntries.map((entry, index) => (
                  <LaterQueueRow
                    key={entry.rowKey}
                    stepNumber={index + 2}
                    entry={entry}
                    operators={
                      entry.kind === "shipment-carton"
                        ? operatorsByRowKey.get(entry.carton.rowKey) ?? []
                        : operatorsByTransferOrderId.get(entry.transfer.transferOrderId) ?? []
                    }
                    viewerUserId={viewerUserId}
                  />
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function isKitCartonLine(item: CartonItem): boolean {
  return item.kitDivisor !== undefined && item.kitDivisor > 1;
}

function formatCartonDevicesPackedSummary(carton: CartonRow): string {
  const totalDevicesPlanned = carton.items.reduce((sum, item) => sum + plannedPhysicalSerials(item), 0);
  const totalDevicesPacked = carton.items.reduce((sum, item) => sum + packedPhysicalSerials(item), 0);
  return `${formatPackQty(totalDevicesPacked)} / ${formatPackQty(totalDevicesPlanned)} devices packed`;
}

function PrePackQueueCartonLineStatus({ item }: { item: CartonItem }) {
  const progress = isKitCartonLine(item) ? formatKitLineProgress(item) : formatLineDeviceProgress(item);
  return (
    <>
      <span className={item.pending > 0 ? "text-amber-800" : "text-emerald-700"}>{progress}</span>
      {item.isSerialized ? null : (
        <span className="ml-2 inline-block whitespace-nowrap rounded bg-white px-1.5 py-0.5 text-[10px] text-slate-600 ring-1 ring-slate-200">
          Unserialized
        </span>
      )}
    </>
  );
}

function PrePackQueueCartonContentsList({
  items,
  variant,
}: {
  items: CartonRow["items"];
  variant: "hero" | "tooltip";
}) {
  const listClassName =
    variant === "hero"
      ? "space-y-1 border-t border-brand-200/60 pt-3 text-sm text-slate-700"
      : "space-y-1 text-sm text-slate-700 min-w-[260px] max-w-md";
  return (
    <ul className={listClassName}>
      {items.map((item, idx) => (
        <li key={`${item.sku}-${item.source}-${idx}`} className="flex items-center justify-between gap-2">
          <span className="truncate font-mono">{item.sku}</span>
          <span className="shrink-0 text-slate-600">
            <PrePackQueueCartonLineStatus item={item} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function NextQueueHero({
  entry,
  operators,
  viewerUserId,
}: {
  entry: PrePackQueueEntry;
  operators: Array<PrePackCartonOperator | PrePackTransferOperator>;
  viewerUserId: string | null;
}) {
  if (entry.kind === "transfer") {
    return <NextTransferHero transfer={entry.transfer} operators={operators} viewerUserId={viewerUserId} />;
  }
  return (
    <NextCartonHero
      carton={entry.carton}
      operators={operators as PrePackCartonOperator[]}
      viewerUserId={viewerUserId}
    />
  );
}

function transferPackQtyPendingBadge(count: number): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <Badge tone="warning">
      {count} pack qty approval
      {count === 1 ? "" : "s"} pending
    </Badge>
  );
}

function NextTransferHero({
  transfer,
  operators,
  viewerUserId,
}: {
  transfer: TransferQueueRow;
  operators: Array<PrePackCartonOperator | PrePackTransferOperator>;
  viewerUserId: string | null;
}) {
  const label = transfer.transferNumber ?? `Transfer #${String(transfer.transferOrderId)}`;
  const to = transferPath(transfer.transferOrderId);
  return (
    <Card className="border-2 border-brand-500 bg-brand-50/40 shadow-md ring-2 ring-brand-500/20">
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-lg font-semibold text-slate-900">{label}</p>
              <Badge tone="info">Transfer</Badge>
              {transferPackQtyPendingBadge(transfer.pendingPackQtyRequestCount)}
            </div>
            <p className="text-sm text-slate-600">
              {formatTransferRouteLabel(transfer)} · Ship by {formatShipDate(transfer.shipByDate)}
            </p>
            <p className="mt-2 text-sm text-slate-700">
              {transfer.pendingPackQtyRequestCount > 0 ? (
                <span className="font-medium text-amber-800">Awaiting pack qty approval</span>
              ) : transfer.isFullyPacked ? (
                <span className="font-medium text-emerald-800">Fully allocated</span>
              ) : (
                <>
                  <span className="font-medium">{transfer.totalPackTarget - transfer.totalAllocated}</span> units
                  remaining · {transfer.totalAllocated} / {transfer.totalPackTarget} allocated
                </>
              )}
            </p>
          </div>
          <div className="flex shrink-0 flex-row items-center justify-end gap-2">
            <PrePackCartonOperatorStack operators={operators} viewerUserId={viewerUserId} />
            <Link
              to={to}
              className={cn(
                "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white",
                "hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
              )}
            >
              Open
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function LaterQueueRow({
  stepNumber,
  entry,
  operators,
  viewerUserId,
}: {
  stepNumber: number;
  entry: PrePackQueueEntry;
  operators: Array<PrePackCartonOperator | PrePackTransferOperator>;
  viewerUserId: string | null;
}) {
  if (entry.kind === "transfer") {
    return (
      <LaterTransferRow
        stepNumber={stepNumber}
        transfer={entry.transfer}
        operators={operators}
        viewerUserId={viewerUserId}
      />
    );
  }
  return (
    <LaterCartonRow
      stepNumber={stepNumber}
      carton={entry.carton}
      operators={operators as PrePackCartonOperator[]}
      viewerUserId={viewerUserId}
    />
  );
}

function LaterTransferRow({
  stepNumber,
  transfer,
  operators,
  viewerUserId,
}: {
  stepNumber: number;
  transfer: TransferQueueRow;
  operators: Array<PrePackCartonOperator | PrePackTransferOperator>;
  viewerUserId: string | null;
}) {
  const label = transfer.transferNumber ?? `Transfer #${String(transfer.transferOrderId)}`;
  const to = transferPath(transfer.transferOrderId);
  return (
    <li>
      <Link
        to={to}
        className={cn(
          "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm",
          "transition-colors hover:border-slate-300 hover:bg-slate-50",
          "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
            {stepNumber}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium text-slate-900">{label}</p>
              <Badge tone="info">Transfer</Badge>
              {transferPackQtyPendingBadge(transfer.pendingPackQtyRequestCount)}
              <PrePackCartonOperatorStack operators={operators} viewerUserId={viewerUserId} />
            </div>
            <p className="text-xs text-slate-500">
              {formatTransferRouteLabel(transfer)} · Ship by {formatShipDate(transfer.shipByDate)}
              {transfer.pendingPackQtyRequestCount > 0 ? (
                <> · <span className="text-amber-800">Awaiting approval</span></>
              ) : (
                <> · {transfer.totalAllocated} / {transfer.totalPackTarget} allocated</>
              )}
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-700">
          Open
          <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      </Link>
    </li>
  );
}

function NextCartonHero({
  carton,
  operators,
  viewerUserId,
}: {
  carton: CartonRow;
  operators: PrePackCartonOperator[];
  viewerUserId: string | null;
}) {
  const totalDevicesPlanned = carton.items.reduce((sum, item) => sum + plannedPhysicalSerials(item), 0);
  const totalDevicesPacked = carton.items.reduce((sum, item) => sum + packedPhysicalSerials(item), 0);
  const totalDevicesPending = carton.items.reduce((sum, item) => sum + pendingPhysicalSerials(item), 0);
  const to = cartonPath(carton.shipmentId, carton.cartonNumber);

  return (
    <Card className="border-2 border-brand-500 bg-brand-50/40 shadow-md ring-2 ring-brand-500/20">
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-lg font-semibold text-slate-900">
              <PrePackCartonShipmentLink carton={carton} />
              {` · Carton ${String(carton.cartonNumber)}`}
            </p>
            <p className="text-sm text-slate-600">
              <PrePackCartonBatchLink carton={carton} />
              {` · Ship by ${formatShipDate(carton.mfrShipByDate)}`}
            </p>
            <p className="mt-2 text-sm text-slate-700">
              {cartonOverTargetCount(carton) > 0 ? (
                <>
                  <span className="font-medium text-rose-800">
                    {formatPackQty(cartonOverTargetCount(carton))} over target
                  </span>
                  <span className="text-slate-600">
                    {" "}
                    · remove the extra before this carton can be sealed · {formatPackQty(totalDevicesPacked)} /{" "}
                    {formatPackQty(totalDevicesPlanned)} packed
                  </span>
                </>
              ) : carton.isFullyPacked ? (
                <>
                  <span className="font-medium text-emerald-800">Fully packed</span>
                  <span className="text-slate-600">
                    {" "}
                    · {formatPackQty(totalDevicesPacked)} / {formatPackQty(totalDevicesPlanned)} devices packed
                  </span>
                </>
              ) : (
                <>
                  <span className="font-medium">{formatPackQty(totalDevicesPending)}</span> devices still pending ·{" "}
                  {formatPackQty(totalDevicesPacked)} / {formatPackQty(totalDevicesPlanned)} devices packed
                </>
              )}
            </p>
          </div>
          <div className="flex shrink-0 flex-row items-center justify-end gap-2">
            <PrePackCartonOperatorStack operators={operators} viewerUserId={viewerUserId} />
            <Link
              to={to}
              className={cn(
                "inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-medium text-white",
                "hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
              )}
            >
              Open
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
        <PrePackQueueCartonContentsList items={carton.items} variant="hero" />
        {(carton.mfrRemark || carton.soInstructions || carton.lineItemInstructions) && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            {carton.mfrRemark ? (
              <p>
                <strong>Mfr note:</strong> {carton.mfrRemark}
              </p>
            ) : null}
            {carton.soInstructions ? (
              <p>
                <strong>SO instructions:</strong> {carton.soInstructions}
              </p>
            ) : null}
            {carton.lineItemInstructions ? (
              <p>
                <strong>Line note:</strong> {carton.lineItemInstructions}
              </p>
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function LaterCartonRow({
  stepNumber,
  carton,
  operators,
  viewerUserId,
}: {
  stepNumber: number;
  carton: CartonRow;
  operators: PrePackCartonOperator[];
  viewerUserId: string | null;
}) {
  const navigate = useNavigate();
  const devicesPackedSummary = formatCartonDevicesPackedSummary(carton);
  const to = cartonPath(carton.shipmentId, carton.cartonNumber);

  return (
    <li>
      <div
        className={cn(
          "flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm",
          "transition-colors hover:border-slate-300 hover:bg-slate-50",
        )}
        onClick={() => navigate(to)}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
            {stepNumber}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium text-slate-900">
                <PrePackCartonShipmentLink carton={carton} />
                {` · Carton ${String(carton.cartonNumber)}`}
              </p>
              <PrePackCartonOperatorStack operators={operators} viewerUserId={viewerUserId} />
            </div>
            <p className="text-xs text-slate-500">
              <PrePackCartonBatchLink carton={carton} />
              {` · Ship by ${formatShipDate(carton.mfrShipByDate)} · `}
              {cartonOverTargetCount(carton) > 0 ? (
                <span className="text-rose-700">
                  {formatPackQty(cartonOverTargetCount(carton))} over target
                </span>
              ) : carton.isFullyPacked ? (
                <span className="text-emerald-700">{devicesPackedSummary}</span>
              ) : (
                <span onClick={isolatePrePackQueueCardClick}>
                  <HoverHintTrigger
                    align="start"
                    trigger={<span>{devicesPackedSummary}</span>}
                  >
                    <PrePackQueueCartonContentsList items={carton.items} variant="tooltip" />
                  </HoverHintTrigger>
                </span>
              )}
            </p>
          </div>
        </div>
        <Link
          to={to}
          onClick={isolatePrePackQueueCardClick}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        >
          Open
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </li>
  );
}
